import {
  CardType,
  MyIELTSDatabase,
  NewCardRecord,
  defaultDb,
  getSettings,
  normalizeNewCardRecord,
  TODAY_SNAPSHOT_ID,
  saveSnapshot,
  saveSettings,
  upsertCards,
} from './indexedDb';
import { getTodayPlan, snapshotFromPlan } from '../srs/dashboard';
import { isAIWriting, isPublicDeploy } from '../config/features';

export const DATASET_VERSION = '2025-12-15-seed-v16';
const SEED_SETTINGS_ID = 'seed-state';

export interface SpeakingTopic {
  region_category?: string;
  part?: string;
  name: string;
  description?: string;
  you_should_say?: string[];
  questions?: string[];
  part3_questions?: string[];
}

export interface WritingSeedCard {
  id?: string;
  skill?: string;
  unitId?: string;
  source?: string;
  prompt?: string;
  answer?: string;
  mode?: string;
  srs?: {
    status?: string;
    interval?: number;
    dueAt?: number | null;
    lapses?: number;
  };
  attempts?: unknown[];
  writingExtra?: {
    topicTag?: string | null;
    subTopicTitle?: string | null;
    cnTranslation?: string | null;
    keyPoints?: string[];
    keyPhrases?: string[];
    hints?: string[];
    highlightPhrases?: { phraseEn?: string; phraseCn?: string }[];
    phase?: string;
    intensity?: string;
    hasEnglish?: boolean;
    csvTopic?: string;
    csvNumber?: string;
    summaries?: string[];
    details?: { summary: string; english: string; cardId: string }[];
    dependsOn?: string[];
    // topic-dictation 模式专用字段
    majorTopic?: string;
    subTopic?: string;
    sentences?: { index: number; english: string; chinese: string }[];
  };
}

export interface ListeningTranscriptRow {
  id: number | string;
  sortNum: number;
  startTime: number;
  endTime: number;
  english: string;
  chinese?: string;
}

export interface ListeningIntensiveFile {
  metadata: {
    cambridge?: string;
    test?: string;
    section?: string;
    title?: string;
    qId?: string;
    intensiveId?: string;
  };
  audio?: {
    url?: string;
    localPath?: string;
  };
  transcript: ListeningTranscriptRow[];
}

export interface VocabEntry {
  id: string;
  term: string;
  definition: string;
  example?: string;
  examples?: string[];
  scene?: string;
  acceptableAnswers?: string[];
  important?: boolean;
  audioPath?: string;
}

export interface SeedSources {
  speakingTopics: SpeakingTopic[];
  writingCards: WritingSeedCard[];
  listeningFiles: ListeningIntensiveFile[];
  vocab?: VocabEntry[];
}

export interface SeedOptions {
  force?: boolean;
  maxPerType?: Partial<Record<CardType, number>>;
  sources?: Partial<SeedSources>;
  now?: number;
  datasetVersionOverride?: string;
}

export interface SeedResult {
  datasetVersion: string;
  inserted: number;
  skippedExisting: number;
  discovered: number;
  skippedVersion: boolean;
  counts: Partial<Record<CardType, number>>;
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 64) || 'item';

const normalizeSpeaking = (topics: SpeakingTopic[], now: number): NewCardRecord[] => {
  return topics.flatMap((topic, topicIdx) => {
    const part = topic.part?.trim() || 'Part 1';
    const cards: NewCardRecord[] = [];

    // Part 1: 用 questions 字段（保持原有 ID 格式，兼容旧数据）
    const questions = topic.questions ?? [];
    questions.forEach((question, index) => {
      const id = `spk-${slugify(topic.name)}-${index + 1}`;
      cards.push({
        id,
        type: 'speaking' as const,
        contentId: id,
        contentVersion: `speaking-${DATASET_VERSION}`,
        topics: [topic.name, part].filter(Boolean),
        mode: 'relearn' as const,
        isRelearn: true,
        relearnBucket: 1,
        nextReviewDate: now,
        content: {
          topic: topic.name,
          part,
          regionCategory: topic.region_category,
          question,
          cues: topic.you_should_say?.filter(Boolean) ?? [],
          followUps: [],
          source: 'speaking-topics-v1',
        },
        speakingMeta: {
          prompt: topic.description,
          question,
          topic: topic.name,
          source: 'speaking-topics-v1',
          extraMeta: { regionCategory: topic.region_category, cues: topic.you_should_say },
        },
      });
    });

    // Part 2: 用 description 字段作为主问题
    if (part === 'Part 2' && topic.description) {
      const slug = slugify(topic.name);
      const id = slug === 'item' ? `spk-p2-${topicIdx + 1}` : `spk-${slug}-p2`;
      cards.push({
        id,
        type: 'speaking' as const,
        contentId: id,
        contentVersion: `speaking-${DATASET_VERSION}`,
        topics: [topic.name, 'Part 2'].filter(Boolean),
        mode: 'relearn' as const,
        isRelearn: true,
        relearnBucket: 1,
        nextReviewDate: now,
        content: {
          topic: topic.name,
          part: 'Part 2',
          regionCategory: topic.region_category,
          question: topic.description,
          cues: topic.you_should_say?.filter(Boolean) ?? [],
          followUps: topic.part3_questions?.filter(Boolean) ?? [],
          source: 'speaking-topics-v1',
        },
        speakingMeta: {
          prompt: topic.description,
          question: topic.description,
          topic: topic.name,
          source: 'speaking-topics-v1',
          extraMeta: { regionCategory: topic.region_category, cues: topic.you_should_say, followUps: topic.part3_questions },
        },
      });
    }

    // Part 3: 每个 part3_question 单独生成卡片
    const part3Questions = topic.part3_questions ?? [];
    part3Questions.forEach((question, index) => {
      const slug = slugify(topic.name);
      const id = slug === 'item' ? `spk-p3-${topicIdx + 1}-${index + 1}` : `spk-${slug}-p3-${index + 1}`;
      cards.push({
        id,
        type: 'speaking' as const,
        contentId: id,
        contentVersion: `speaking-${DATASET_VERSION}`,
        topics: [topic.name, 'Part 3'].filter(Boolean),
        mode: 'relearn' as const,
        isRelearn: true,
        relearnBucket: 1,
        nextReviewDate: now,
        content: {
          topic: topic.name,
          part: 'Part 3',
          regionCategory: topic.region_category,
          question,
          cues: [],
          followUps: [],
          relatedPart2: topic.description,
          source: 'speaking-topics-v1',
        },
        speakingMeta: {
          prompt: topic.description,
          question,
          topic: topic.name,
          source: 'speaking-topics-v1',
          extraMeta: { regionCategory: topic.region_category, relatedPart2: topic.description },
        },
      });
    });

    return cards;
  });
};

const normalizeWriting = (cards: WritingSeedCard[], now: number): NewCardRecord[] => {
  return cards.map((item, index) => {
    const id = item.id || `wrt-${slugify(item.unitId ?? 'writing')}-${index + 1}`;
    const dueAt = typeof item.srs?.dueAt === 'number' ? item.srs?.dueAt ?? now : now;

    // 确定 mode：保留 translation/argument/topic-recall/topic-dictation，其他默认为 relearn
    const mode = (['translation', 'argument', 'topic-recall', 'topic-dictation'].includes(item.mode ?? ''))
      ? item.mode as 'translation' | 'argument' | 'topic-recall' | 'topic-dictation'
      : 'relearn' as const;

    // 对于 topic-dictation 模式，需要特殊处理 content 结构
    const isTopicDictation = mode === 'topic-dictation';
    const content = isTopicDictation
      ? {
          majorTopic: item.writingExtra?.majorTopic || item.writingExtra?.topicTag,
          subTopic: item.writingExtra?.subTopic || item.writingExtra?.subTopicTitle,
          sentences: item.writingExtra?.sentences ?? [],
          source: item.source ?? 'simonTopicDictation.json',
        }
      : {
          prompt: item.prompt,
          answer: item.answer,
          topicTag: item.writingExtra?.topicTag,
          keyPoints: item.writingExtra?.keyPoints ?? [],
          keyPhrases: item.writingExtra?.keyPhrases ?? [],
          hints: item.writingExtra?.hints ?? [],
          summaries: item.writingExtra?.summaries ?? [],
          details: item.writingExtra?.details ?? [],
          dependsOn: item.writingExtra?.dependsOn ?? [],
          source: item.source ?? 'writingSeedCards.json',
          phase: item.writingExtra?.phase,
          intensity: item.writingExtra?.intensity,
          csvTopic: item.writingExtra?.csvTopic,
          csvNumber: item.writingExtra?.csvNumber,
        };

    return {
      id,
      type: 'writing' as const,
      contentId: id,
      contentVersion: `writing-${DATASET_VERSION}`,
      topics: [item.writingExtra?.topicTag, item.writingExtra?.csvTopic, 'writing'].filter(Boolean) as string[],
      mode,
      isRelearn: mode === 'relearn',
      relearnBucket: 1,
      nextReviewDate: dueAt ?? now,
      content,
      writingMeta: {
        cnHint: item.writingExtra?.topicTag ?? undefined,
        enOriginal: item.answer,
        cnTranslation: item.writingExtra?.cnTranslation ?? undefined,
        keyPhrases: item.writingExtra?.keyPhrases ?? [],
        keyPoints: item.writingExtra?.keyPoints ?? [],
        topics: item.writingExtra?.keyPoints,
        topicTag: item.writingExtra?.topicTag,
        summaries: item.writingExtra?.summaries ?? [],
        details: item.writingExtra?.details ?? [],
        dependsOn: item.writingExtra?.dependsOn ?? [],
        bestVersion: item.answer,
        synonymWhitelistId: item.writingExtra?.csvNumber ?? undefined,
        extraMeta: {
          phase: item.writingExtra?.phase,
          intensity: item.writingExtra?.intensity,
          subTopicTitle: item.writingExtra?.subTopicTitle,
          keyPoints: item.writingExtra?.keyPoints,
          hints: item.writingExtra?.hints,
        },
      },
    };
  });
};

const normalizeListening = (files: ListeningIntensiveFile[], now: number): NewCardRecord[] => {
  return files.flatMap((file) => {
    const meta = file.metadata ?? {};
    let lastEnd = -Infinity;

    return file.transcript.flatMap((row) => {
      const segmentId = typeof row.id === 'number' ? row.id.toString() : row.id;
      const id = `lst-${meta.qId ?? slugify(meta.title ?? 'listening')}-${row.sortNum}`;
      const startMs = Math.round(row.startTime * 1000);
      const endMs = Math.round(row.endTime * 1000);
      const invalid = !Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs >= endMs || startMs < lastEnd;
      lastEnd = Math.max(lastEnd, endMs);

      if (invalid) {
        console.warn(
          `[seed] skip listening segment ${meta.qId ?? meta.title ?? 'unknown'} #${row.sortNum} due to invalid timestamps (${startMs}-${endMs})`
        );
        return [];
      }

      const rawLocalPath = file.audio?.localPath;
      const audioPath = rawLocalPath ? `/${rawLocalPath.replace(/^\/+/, '')}` : undefined;
      const fallbackUrl = file.audio?.url;
      const audioMissing = !audioPath && !fallbackUrl;
      return [
        {
          id,
          contentId: id,
          contentVersion: `listening-${DATASET_VERSION}`,
          type: 'listening' as const,
          topics: [meta.title, meta.section, meta.test, 'listening'].filter(Boolean) as string[],
          mode: 'relearn' as const,
          isRelearn: true,
          relearnBucket: 1,
          nextReviewDate: now,
          content: {
            meta,
            audioUrl: audioPath ?? fallbackUrl,
            fallbackAudioUrl: fallbackUrl ?? audioPath,
            segment: {
              id: segmentId,
              index: row.sortNum,
              startMs,
              endMs,
              durationMs: Math.max(endMs - startMs, 0),
              english: row.english,
              chinese: row.chinese,
            },
            audioMissing,
            source: 'listening-intensive-v1',
          },
          listeningMeta: {
            materialId: meta.intensiveId ?? meta.qId,
            sourceId: meta.qId ?? meta.title,
            sentenceId: segmentId,
            audioPath,
            audioKey: rawLocalPath,
            start: startMs,
            end: endMs,
            transcript: row.english,
            clozeTemplate: row.english,
            clozeIndexes: [],
            initialMode: 'relearn',
            audioMissing,
            extraMeta: { title: meta.title, chinese: row.chinese },
          },
        } satisfies NewCardRecord,
      ];
    });
  });
};

const normalizeVocab = (entries: VocabEntry[] = [], now: number): NewCardRecord[] => {
  return entries.map((entry, index) => {
    const examples = Array.isArray(entry.examples)
      ? entry.examples.filter(Boolean)
      : entry.example
        ? [entry.example]
        : [];
    const acceptableAnswers = entry.acceptableAnswers ?? [];
    const baseId = entry.id || `vocab-${index + 1}`;
    return {
      id: baseId,
      type: 'vocab' as const,
      contentId: baseId,
      contentVersion: `vocab-${DATASET_VERSION}`,
      topics: entry.example ? [entry.example] : ['vocab'],
      mode: 'relearn' as const,
      isRelearn: true,
      relearnBucket: 1,
      nextReviewDate: now,
      content: {
        term: entry.term,
        definition: entry.definition,
        example: entry.example,
        examples,
        scene: entry.scene,
        audioPath: entry.audioPath,
        source: 'vocab-seed-v1',
      },
      vocabMeta: {
        en: entry.term,
        cn: entry.definition,
        scene: entry.scene,
        examples,
        acceptableAnswers: [...acceptableAnswers, entry.term],
        important: entry.important,
        front: entry.term,
        back: entry.definition,
        exampleSentence: entry.example,
        scenarioTopics: entry.id ? [entry.id] : [],
      },
    };
  });
};

const applyLimits = (cards: NewCardRecord[], maxPerType?: Partial<Record<CardType, number>>) => {
  if (!maxPerType) return cards;

  const grouped = new Map<CardType, NewCardRecord[]>();
  for (const card of cards) {
    const list = grouped.get(card.type) ?? [];
    list.push(card);
    grouped.set(card.type, list);
  }

  const limited: NewCardRecord[] = [];
  for (const [type, list] of grouped.entries()) {
    const limit = maxPerType[type];
    const trimmed = typeof limit === 'number' ? list.slice(0, Math.max(limit, 0)) : list;
    limited.push(...trimmed);
  }

  return limited;
};

export const buildCardsFromSources = (sources: SeedSources, now: number, maxPerType?: Partial<Record<CardType, number>>) => {
  const speakingCards = normalizeSpeaking(sources.speakingTopics ?? [], now);
  const writingCards = normalizeWriting(sources.writingCards ?? [], now);
  const listeningCards = normalizeListening(sources.listeningFiles ?? [], now);
  const vocabCards = normalizeVocab(sources.vocab ?? [], now);

  const all = [...speakingCards, ...writingCards, ...listeningCards, ...vocabCards];
  const deduped = new Map<string, NewCardRecord>();
  for (const card of all) {
    if (!deduped.has(card.id)) {
      deduped.set(card.id, card);
    }
  }

  const limited = applyLimits([...deduped.values()], maxPerType);
  const annotated = limited.map((card) => ({
    ...card,
    contentId: card.contentId ?? card.id,
    contentVersion: card.contentVersion ?? DATASET_VERSION,
  }));
  return annotated.sort((a, b) => a.id.localeCompare(b.id));
};

const fetchJson = async <T>(url: string): Promise<T> => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
};

async function loadSpeakingTopics(): Promise<SpeakingTopic[]> {
  const url = new URL('/ielts_data/speaking/ielts_topics_final_filtered.json', location.origin).toString();
  const data = await fetchJson<{ topics?: SpeakingTopic[] }>(url);
  return data.topics ?? [];
}

async function loadWritingCards(): Promise<WritingSeedCard[]> {
  // 加载 Simon 话题默写题库
  const loadSimonCards = async (): Promise<WritingSeedCard[]> => {
    try {
      const url = new URL('/ielts_data/writing/simonTopicDictation.json', location.origin).toString();
      const cards = await fetchJson<WritingSeedCard[]>(url);
      console.log(`[seed] Loaded ${cards.length} Simon topic-dictation cards`);
      return cards;
    } catch (err) {
      console.warn('[seed] Failed to load Simon topic-dictation cards:', err);
      return [];
    }
  };

  // Cloudflare 私有版：只加载话题默写卡片
  if (!isPublicDeploy) {
    console.log('[seed] Cloudflare private mode: loading topic-dictation cards only');
    const simonCards = await loadSimonCards();
    console.log(`[seed] Loaded ${simonCards.length} topic-dictation cards`);
    return simonCards;
  }

  // 公开版：根据配置加载单一类型 + Simon 卡片
  if (isAIWriting) {
    console.log('[seed] AI writing mode: loading AI writing cards');
    const [aiCards, simonCards] = await Promise.all([
      fetchJson<any[]>(new URL('/ielts_data/writing/aiWritingCards.json', location.origin).toString()),
      loadSimonCards(),
    ]);
    // 转换 AI 卡片格式为 WritingSeedCard 格式
    const convertedAiCards: WritingSeedCard[] = aiCards.map((card) => ({
      id: card.id,
      skill: 'writing',
      unitId: card.topic,
      source: 'ai-generated',
      prompt: card.type === 'translation' ? card.chinese : card.argument,
      answer: card.type === 'translation' ? card.english : card.reference,
      mode: card.type, // 'translation' or 'argument'
      writingExtra: {
        topicTag: card.topic,
        keyPoints: card.type === 'translation' ? card.keywords : card.keyPoints,
      },
    }));
    return [...convertedAiCards, ...simonCards];
  }

  const [staticCards, simonCards] = await Promise.all([
    fetchJson<WritingSeedCard[]>(new URL('/ielts_data/writing/writingSeedCards.json', location.origin).toString()),
    loadSimonCards(),
  ]);
  return [...staticCards, ...simonCards];
}

async function loadListeningFiles(): Promise<ListeningIntensiveFile[]> {
  const modules = import.meta.glob('../../ielts_data/listening/**/*_intensive.json', {
    eager: true,
    import: 'default',
    query: '?url',
  });
  const urls = Object.values(modules) as string[];
  return Promise.all(urls.map((url) => fetchJson<ListeningIntensiveFile>(url)));
}

async function mergeSources(overrides?: Partial<SeedSources>): Promise<SeedSources> {
  const [speakingTopics, writingCards, listeningFiles] = await Promise.all([
    overrides?.speakingTopics ?? loadSpeakingTopics(),
    overrides?.writingCards ?? loadWritingCards(),
    overrides?.listeningFiles ?? loadListeningFiles(),
  ]);

  return {
    speakingTopics,
    writingCards,
    listeningFiles,
    vocab: overrides?.vocab ?? [],
  };
}

export async function seedInitialData(db: MyIELTSDatabase = defaultDb, options: SeedOptions = {}): Promise<SeedResult> {
  const now = options.now ?? Date.now();
  const datasetVersion = options.datasetVersionOverride ?? DATASET_VERSION;

  const seedSettings = await getSettings(SEED_SETTINGS_ID, db);
  const alreadySeeded = seedSettings?.preferences?.datasetVersion === datasetVersion;

  if (alreadySeeded && !options.force) {
    return {
      datasetVersion,
      inserted: 0,
      skippedExisting: 0,
      discovered: seedSettings?.preferences?.totalDiscovered ?? 0,
      skippedVersion: true,
      counts: (seedSettings?.preferences?.counts ?? {}) as Partial<Record<CardType, number>>,
    };
  }

  const sources = await mergeSources(options.sources);
  const candidates = buildCardsFromSources(sources, now, options.maxPerType);
  const counts = candidates.reduce<Partial<Record<CardType, number>>>((acc, card) => {
    acc[card.type] = (acc[card.type] ?? 0) + 1;
    return acc;
  }, {});

  const { inserted } = await upsertCards(candidates, db, now);
  const skippedExisting = Math.max(candidates.length - inserted, 0);

  await saveSettings(
    {
      id: SEED_SETTINGS_ID,
      preferences: {
        datasetVersion,
        lastImportedAt: now,
        totalDiscovered: candidates.length,
        totalInserted: inserted,
        skippedExisting,
        counts,
        maxPerType: options.maxPerType,
      },
    },
    db
  );

  const { plan } = await getTodayPlan(db, now);
  await saveSnapshot(snapshotFromPlan(plan, now, 'snapshot from seedInitialData'), db);

  return {
    datasetVersion,
    inserted,
    skippedExisting,
    discovered: candidates.length,
    skippedVersion: false,
    counts,
  };
}
